import React from 'react';
import { FiActivity, FiArrowDown, FiInfo } from 'react-icons/fi';
import type {
  LeakageCascade,
  LeakageReason,
  LeakageReasonsByTransition,
  LeakageStage,
  LeakageTransitionKey,
} from '@troia-v3/types';

import { DashboardSectionCard } from './DashboardSectionCard';

interface LeakageCascadeCardProps {
  cascade: LeakageCascade;
  /** `true` quando reasonsByTransition vem vazio (sem `Lead.lossReason` ainda). */
  reasonsAreSynthetic?: boolean;
}

/**
 * Anatomia alinhada ao mockup (dashboard-marketing.html `.casc-stage`/`.casc-drop`):
 * cada estágio é um card de superfície em LINHA ÚNICA (dot roxo + nome + valor + %),
 * e a perda entre estágios aparece em VERMELHO (var(--error) do design system).
 */
const STAGE_DOT_COLOR = '#8b5cf6';
const DROP_COLOR = '#ef4444';

const TRANSITION_LABEL: Record<LeakageTransitionKey, string> = {
  visitors_to_leads: 'Visitantes → Leads',
  leads_to_proposals: 'Leads → Propostas',
  proposals_to_won: 'Propostas → Ganhos',
};

const formatNumber = (v: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(v);

const formatPct = (v: number): string =>
  `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(v)}%`;

interface CascadeStageProps {
  stage: LeakageStage;
  isLast: boolean;
  indentationStep: number;
}

const CascadeStage: React.FC<CascadeStageProps> = ({ stage, isLast, indentationStep }) => {
  const indent = indentationStep * 18; // px — um degrau de recuo por transição (mockup: ind*18px)

  return (
    <div className="flex flex-col" data-testid={`leakage-stage-${stage.key}`}>
      <div
        className="flex items-center gap-2.5 rounded-lg p-3"
        style={{
          marginLeft: `${indent}px`,
          backgroundColor: 'var(--theme-card-background)',
          border: '1px solid var(--theme-card-border)',
        }}
      >
        <span
          className="flex-none rounded-full"
          style={{ width: 9, height: 9, backgroundColor: STAGE_DOT_COLOR }}
        />
        <span
          className="flex-1 text-sm font-semibold"
          style={{ color: 'var(--theme-card-text)' }}
        >
          {stage.label}
        </span>
        <span
          className="text-sm font-bold tabular-nums"
          style={{ color: 'var(--theme-card-text)' }}
        >
          {formatNumber(stage.count)}
        </span>
        <span
          className="w-[52px] text-right text-xs tabular-nums"
          style={{ color: 'var(--theme-muted-text)' }}
        >
          {formatPct(stage.sharePct)}
        </span>
      </div>
      {!isLast && stage.lostToNext > 0 && (
        <div
          className="flex items-center gap-1.5 py-1.5 text-xs"
          style={{ marginLeft: `${indent}px`, color: DROP_COLOR }}
        >
          <span
            className="flex-none"
            style={{ width: 1, height: 16, margin: '0 14px 0 5px', backgroundColor: 'var(--theme-card-border)' }}
          />
          <FiArrowDown size={12} />
          <span>
            -{formatNumber(stage.lostToNext)} ({formatPct(stage.lostPct)} não avançaram)
          </span>
        </div>
      )}
    </div>
  );
};

interface ReasonGroupProps {
  transition: LeakageTransitionKey;
  reasons: LeakageReason[];
  reasonsAreSynthetic: boolean;
}

const ReasonGroup: React.FC<ReasonGroupProps> = ({ transition, reasons, reasonsAreSynthetic }) => {
  // Defesa: payload pode chegar com chaves desatualizadas (cache do React Query
  // entre deploys que renomearam keys de transição). Trata undefined como vazio.
  const safeReasons: LeakageReason[] = Array.isArray(reasons) ? reasons : [];
  const max = safeReasons.reduce((acc, r) => Math.max(acc, r.count), 0);

  return (
    <div data-testid={`leakage-reason-group-${transition}`}>
      <div
        className="text-[11px] uppercase tracking-wide font-semibold mb-2"
        style={{ color: 'var(--theme-muted-text)' }}
      >
        {TRANSITION_LABEL[transition]}
      </div>
      {safeReasons.length === 0 ? (
        <div
          className="text-xs flex items-center gap-1.5 px-2 py-2 rounded-md"
          style={{
            color: 'var(--theme-muted-text)',
            backgroundColor: 'var(--theme-page-background)',
            border: '1px dashed var(--theme-card-border)',
          }}
        >
          <FiInfo size={11} />
          {reasonsAreSynthetic
            ? 'Razões serão exibidas quando o motivo de perda for cadastrado'
            : 'Sem razões registradas'}
        </div>
      ) : (
        <div className="space-y-1.5">
          {safeReasons.map((r) => {
            const widthPct = max === 0 ? 0 : (r.count / max) * 100;
            return (
              <div key={r.reason} className="flex items-center gap-2 text-xs">
                <span
                  className="flex-shrink-0 truncate w-32"
                  style={{ color: 'var(--theme-card-text)' }}
                  title={r.reason}
                >
                  {r.reason}
                </span>
                <div
                  className="flex-1 h-1.5 rounded-full"
                  style={{ backgroundColor: 'var(--theme-card-border)' }}
                >
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.max(2, widthPct)}%`,
                      backgroundColor: 'rgba(139, 92, 246, 0.6)',
                    }}
                  />
                </div>
                <span
                  className="flex-shrink-0 w-10 text-right font-medium"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  {formatNumber(r.count)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const LeakageCascadeCard: React.FC<LeakageCascadeCardProps> = ({
  cascade,
  reasonsAreSynthetic = false,
}) => {
  const transitions: LeakageTransitionKey[] = [
    'visitors_to_leads',
    'leads_to_proposals',
    'proposals_to_won',
  ];

  return (
    <DashboardSectionCard
      icon={<FiActivity size={16} style={{ color: '#7c3aed' }} />}
      title="Cascata de perdas"
      subtitle="Onde o funil mais vaza, com volumes e razões agregadas por transição."
      badge={
        reasonsAreSynthetic ? (
          <span
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full"
            style={{ backgroundColor: 'var(--theme-page-background)', color: 'var(--theme-muted-text)' }}
            title="Razões de perda serão exibidas quando o campo `lossReason` for adicionado ao Lead."
          >
            <FiInfo size={11} />
            razões em construção
          </span>
        ) : undefined
      }
      testId="leakage-cascade-card"
    >
      <div className="grid grid-cols-1 min-[900px]:grid-cols-[1.1fr_1fr] gap-6">
        <div className="space-y-1">
          {cascade.stages.length === 0 ? (
            <div
              className="text-sm text-center px-4 py-8 rounded-md"
              style={{
                backgroundColor: 'var(--theme-page-background)',
                color: 'var(--theme-muted-text)',
              }}
              data-testid="leakage-cascade-empty"
            >
              Sem dados no período.
            </div>
          ) : (
            cascade.stages.map((stage, idx) => (
              <CascadeStage
                key={stage.key}
                stage={stage}
                isLast={idx === cascade.stages.length - 1}
                indentationStep={idx}
              />
            ))
          )}
        </div>
        <div className="space-y-5">
          {transitions.map((t) => (
            <ReasonGroup
              key={t}
              transition={t}
              reasons={(cascade.reasonsByTransition as Partial<LeakageReasonsByTransition> | undefined)?.[t] ?? []}
              reasonsAreSynthetic={reasonsAreSynthetic}
            />
          ))}
        </div>
      </div>
    </DashboardSectionCard>
  );
};

LeakageCascadeCard.displayName = 'LeakageCascadeCard';
